import * as React from 'react';

import { EditOutlined, CopyOutlined, UndoOutlined, InboxOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import { Table, Button, Space, Switch, Tag, Tooltip, Popconfirm } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { RuleTableItem, PredictionModel } from '../../types';

// Статусы обучения (справочник suggest_learning_statuses)
const LEARNING_STATUS_QUEUED = 2;
const LEARNING_STATUS_TRAINING = 3;
const LEARNING_STATUS_COMPLETED = 4;
const LEARNING_STATUS_INSUFFICIENT_DATA = 5;

// Пороги по числу покупателей — те же, что в ML-сервисе (prediction-service/DataGenerator.py)
const MIN_TARGET_USERS = 100;
const RELIABLE_TARGET_USERS = 100;

interface RulesTableProps {
  apiModels: PredictionModel[];
  loading?: boolean;
  pagination?: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
  onEdit: (rule: RuleTableItem) => void;
  onDelete: (id: string) => void;
  onUnarchive?: (id: string) => void;
  onToggleStatus: (id: string, isActive: boolean) => void;
  onAdd: () => void;
  onDuplicate?: (rule: RuleTableItem) => void;
  onResetActions?: (id: string) => void;
  onTableChange?: (pagination: any, filters: any, sorter: any) => void;
  activeTab?: string;
  onModelClick?: (rule: RuleTableItem) => void;
}

export const RulesTable: React.FC<RulesTableProps> = ({
  apiModels,
  loading = false,
  pagination,
  onEdit,
  onDelete,
  onUnarchive,
  onToggleStatus,
  onDuplicate,
  onResetActions,
  onTableChange,
  activeTab = 'active',
  onModelClick
}) => {
  // API Columns with sorting
  const columns: ColumnsType<PredictionModel> = [
    {
      title: 'Название модели',
      dataIndex: 'model_name',
      key: 'model_name',
      width: 250,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      render: (name: string, record: PredictionModel) => (
        <>
          <span
            style={{
              color: '#1677ff',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
            onClick={() => onModelClick?.(convertApiModelToRuleTableItem(record))}
          >
            {name}
          </span>
          {record.last_action_error && (
            <Tooltip
              title={
                <>
                  {record.last_action_error}
                  {record.last_action_error_at && <div style={{ marginTop: 4, opacity: 0.8 }}>{record.last_action_error_at}</div>}
                  <div style={{ marginTop: 4 }}>Исправьте действие в модели и сохраните — отметка снимется.</div>
                </>
              }
            >
              <div style={{ marginTop: 4 }}>
                <Tag color="red" icon={<WarningOutlined />} style={{ cursor: 'help' }}>Ошибка действия</Tag>
              </div>
            </Tooltip>
          )}
        </>
      ),
    },
    {
      title: 'Статус обучения',
      dataIndex: 'learning_status',
      key: 'learning_status',
      width: 150,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      render: (status: any, record: PredictionModel) => {
        if (!status || !status.name) return '-';

        const targetUsers = record.target_users;

        // Модель не работает: ошибка обучения или прогноза. Подробности — в нашей группе уведомлений
        if (record.model_error_at) {
          return (
            <Tooltip
              title={`С ${record.model_error_at} прогноз по модели временно не обновляется. Мы получили уведомление и уже разбираемся — делать ничего не нужно.`}
            >
              <Tag color="red" style={{ cursor: 'help' }}>Ошибка</Tag>
            </Tooltip>
          );
        }

        // Недостаточно данных: покупателей меньше минимума, модель не обучилась
        if (status.id === LEARNING_STATUS_INSUFFICIENT_DATA) {
          return (
            <Tooltip
              title={
                typeof targetUsers === 'number'
                  ? `Покупателей за период: ${targetUsers}, нужно минимум ${MIN_TARGET_USERS}. На меньшем числе модель не отличает тех, кто готов купить, от остальных. Так бывает, когда в фильтре одно предложение с небольшим числом продаж: если вам важна вся линейка, а не отдельный тариф, добавьте в фильтр похожие предложения.`
                  : `Для обучения нужно минимум ${MIN_TARGET_USERS} покупателей за период.`
              }
            >
              <Tag color="red">
                {typeof targetUsers === 'number'
                  ? `Недостаточно данных: ${targetUsers} из ${MIN_TARGET_USERS}`
                  : status.name}
              </Tag>
            </Tooltip>
          );
        }

        // Обучена, но покупателей мало — прогноз менее надёжный
        if (
          status.id === LEARNING_STATUS_COMPLETED &&
          typeof targetUsers === 'number' &&
          targetUsers < RELIABLE_TARGET_USERS
        ) {
          return (
            <Tooltip
              title={`Покупателей за период: ${targetUsers}, для уверенного прогноза нужно от ${RELIABLE_TARGET_USERS}. Модель работает, но ошибается чаще — так бывает, когда в фильтре одно предложение с небольшим числом продаж. Если вам важна вся линейка, а не отдельный тариф, добавьте в фильтр похожие предложения: покупателей станет больше, прогноз точнее.`}
            >
              <Tag color="orange">Обучено, мало данных: {targetUsers}</Tag>
            </Tooltip>
          );
        }

        const colors: Record<number, string> = {
          [LEARNING_STATUS_COMPLETED]: 'green',
          [LEARNING_STATUS_QUEUED]: 'blue',
          [LEARNING_STATUS_TRAINING]: 'blue',
        };

        return <Tag color={colors[status.id] || 'default'}>{status.name}</Tag>;
      },
    },
    {
      title: 'Точность',
      dataIndex: 'prediction_accuracy',
      key: 'prediction_accuracy',
      width: 100,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      render: (accuracy: string) => accuracy || '-',
    },
    {
      title: 'Последнее обучение',
      dataIndex: 'last_learning',
      key: 'last_learning',
      width: 150,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      render: (lastLearning: string) => lastLearning || '-',
    },
    {
      title: 'Условия и действия',
      dataIndex: 'conditions_and_actions',
      key: 'conditions_and_actions',
      width: 400,
      render: (conditions: string[]) => {
        if (!conditions || conditions.length === 0) return '-';
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {conditions.map((condition, index) => (
              <div key={index} style={{ fontSize: '13px', color: '#595959' }}>
                {index + 1}. {condition}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      title: 'Статус',
      dataIndex: 'model_status',
      key: 'model_status', // ключ сортировки — сервер знает model_status, а не status
      width: 100,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      render: (status: any, record: PredictionModel) => (
        <Switch
          checked={status?.name === 'Включена'}
          onChange={(checked) => onToggleStatus(record.id.toString(), checked)}
          disabled={activeTab === 'archived'}
        />
      ),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 200,
      render: (_, record: PredictionModel) => (
        <Space>
          {activeTab === 'active' ? (
            <>
              <Tooltip title="Редактировать">
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => onEdit(convertApiModelToRuleTableItem(record))}
                />
              </Tooltip>
              <Tooltip title="Дублировать">
                <Button
                  type="text"
                  icon={<CopyOutlined />}
                  onClick={() => onDuplicate?.(convertApiModelToRuleTableItem(record))}
                />
              </Tooltip>
              <Popconfirm
                title={
                  <div style={{ maxWidth: 320 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>Выполнить действия заново?</div>
                    История выполненных действий этой модели будет очищена.
                    При ближайшем прогнозе все действия выполнятся заново для всех подходящих учеников.
                  </div>
                }
                okText="Выполнить заново"
                cancelText="Отмена"
                okButtonProps={{ danger: true }}
                onConfirm={() => onResetActions?.(record.id.toString())}
              >
                <Tooltip title="Выполнить действия заново">
                  <Button type="text" icon={<ReloadOutlined />} />
                </Tooltip>
              </Popconfirm>
              <Tooltip title="Архивировать">
                <Button
                  type="text"
                  icon={<InboxOutlined />}
                  onClick={() => onDelete(record.id.toString())}
                />
              </Tooltip>
            </>
          ) : (
            <Tooltip title="Восстановить">
              <Button
                type="text"
                icon={<UndoOutlined />}
                onClick={() => onUnarchive?.(record.id.toString())}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  // Convert API model to RuleTableItem for compatibility
  const convertApiModelToRuleTableItem = (apiModel: PredictionModel): RuleTableItem => {
    return {
      id: apiModel.id.toString(),
      name: apiModel.model_name,
      periodDays: 7, // Default value
      groups: [],
      conditions: [],
      actions: {
        budgetAction: 'none',
        budgetAmount: 0,
        budgetUnit: 'rub',
        sendTelegram: false,
        tgMessage: '',
        tgRecipients: []
      },
      filters: [], // TODO: Преобразовать из API данных когда будут доступны
      isActive: apiModel.model_status?.name === 'Включена',
      isArchived: activeTab === 'archived',
      createdAt: new Date().toLocaleDateString('ru-RU'),
      createdBy: 'API',
      lastChecked: apiModel.last_learning !== '—' ? apiModel.last_learning : undefined,
      trainingStatus: apiModel.learning_status?.name === 'Обучена' ? 'success' :
        apiModel.learning_status?.name === 'Обучение' ? 'training' : 'insufficient_data',
      accuracy: apiModel.prediction_accuracy !== '—' ? parseInt(apiModel.prediction_accuracy) : 0
    };
  };

  return (
    <Table
      columns={columns}
      dataSource={apiModels}
      rowKey="id"
      scroll={{ x: 1200 }}
      size="small"
      loading={loading}
      onChange={onTableChange}
      pagination={pagination ? {
        current: pagination.currentPage,
        pageSize: pagination.pageSize,
        total: pagination.totalCount,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) => `Показано ${range[0]}-${range[1]} из ${total}`,
      } : false}
    />
  );
};

